import type { SchedulingApiEnvelope } from "./workspace-scheduling.types";

export type RoutingPublicFieldType =
  | "text"
  | "textarea"
  | "email"
  | "phone"
  | "number"
  | "select"
  | "dropdown"
  | "multi_select"
  | "checkbox"
  | "radio"
  | "boolean"
  | "date"
  | "datetime"
  | string;

export type RoutingPublicTargetType =
  | "event_type"
  | "user"
  | "url"
  | "fallback"
  | string;

export type RoutingPublicJsonValue =
  | string
  | number
  | boolean
  | null
  | RoutingPublicJsonValue[]
  | { [key: string]: RoutingPublicJsonValue };
export type RoutingPublicJsonRecord = Record<string, RoutingPublicJsonValue>;

export interface RoutingPublicFieldOption {
  label?: string;
  value?: string | number | boolean;
  isActive?: boolean;
  [key: string]: RoutingPublicJsonValue | undefined;
}

export interface RoutingPublicField {
  id: number;
  label: string;
  fieldKey: string;
  fieldType: RoutingPublicFieldType;
  placeholder?: string | null;
  helpText?: string | null;
  isRequired: boolean;
  options?:
    | RoutingPublicJsonRecord
    | RoutingPublicFieldOption[]
    | RoutingPublicJsonValue[]
    | null;
  validationRules?: RoutingPublicJsonRecord | null;
  sortOrder: number;
}

export interface RoutingPublicFormSettings {
  submitButtonLabel?: string;
  successMessage?: string;
  [key: string]: RoutingPublicJsonValue | undefined;
}

export interface RoutingPublicForm {
  id?: number;
  workspaceId: number;
  name: string;
  slug: string;
  publicKey: string;
  title: string;
  description?: string | null;
  fallbackEventTypeId?: number | null;
  successRedirectUrl?: string | null;
  settings?: RoutingPublicFormSettings | RoutingPublicJsonRecord | null;
  fields: RoutingPublicField[];
}

export interface RoutingPublicSubmitRequest {
  inviteeName?: string;
  inviteeEmail?: string;
  inviteePhone?: string;
  answers: RoutingPublicJsonRecord;
  source: string;
}

export interface RoutingPublicSubmitInput
  extends Partial<RoutingPublicSubmitRequest> {
  publicKey: string;
  answers: RoutingPublicJsonRecord;
  source: string;
}

export interface RoutingPublicSubmitData {
  submissionReference?: string | null;
  submissionId?: number | null;
  matchedRuleId?: number | null;
  targetType?: RoutingPublicTargetType | null;
  routedEventTypeId?: number | null;
  bookingPublicKey?: string | null;
  bookingUrl?: string | null;
  targetUserId?: number | null;
  targetUrl?: string | null;
}

export interface RoutingPublicSubmitResult {
  data: RoutingPublicSubmitData;
  message: string;
}

export type RoutingPublicFormResponse =
  SchedulingApiEnvelope<RoutingPublicForm>;
export type RoutingPublicSubmitResponse =
  SchedulingApiEnvelope<RoutingPublicSubmitData>;

export type GetRoutingPublicFormInput =
  | string
  | {
      publicKey: string;
    };

export const ROUTING_PUBLIC_VALIDATION_MESSAGES = {
  PUBLIC_KEY_REQUIRED: "Routing form public key is required.",
  INVALID_PUBLIC_KEY: "Routing form public key is invalid.",
  INVITEE_NAME_REQUIRED: "Invitee name is required.",
  INVITEE_NAME_TOO_LONG: "Invitee name must be 180 characters or fewer.",
  INVITEE_EMAIL_REQUIRED: "Invitee email is required.",
  INVALID_INVITEE_EMAIL: "Invitee email is invalid.",
  INVITEE_PHONE_TOO_LONG: "Invitee phone must be 50 characters or fewer.",
  ANSWERS_REQUIRED: "Routing form answers are required.",
  INVALID_ANSWERS: "Routing form answers must be an object.",
  SOURCE_REQUIRED: "Source is required.",
  SOURCE_TOO_LONG: "Source must be 120 characters or fewer.",
  INVALID_RESPONSE: "Routing public API returned an unexpected response.",
} as const;
